import React, { useCallback, useEffect, useState } from "react";

// API
import api from "../../../api/api";

// Components
import StackStyle from "../../Styled/Stack.style";
import Modal from "../../Modal/Modal";
import QueueStyle from "../../Styled/Queue.style";
import { ModelSelect } from "../../Controls/ModelSelect";
import { RangeSelect } from "../../Controls/RangeSelect";
import { BrandSelect } from "../../Controls/BrandSelect";
import { MMCodeInput } from "../../Controls/MMCodeInput";
import { SubmitButton } from "../../Controls/SubmitButton";
import SearchModal from "../Search/SearchModal";

// Utils
import useType from "../../../utils/hooks/useType";
import useAsync from "../../../utils/hooks/useAsync";
import hasNoValue from "../../../utils/helpers/hasNoValue";
import { findObjectDifferences, getObjectDiff } from "../../../utils/helpers/findObjectDifferences";
import {
  CONDITIONS,
  DEAL_STATUSES,
  DEAL_TYPES,
  API_RESPONSE,
  RATE_TYPES,
  SCOPES,
  USER_KEY,
} from "../../../utils/constants/SYSTEM";
import { useRef } from "react";
import { get } from "../../../utils/helpers/storage";
import { CurrencyInput } from "../../Controls/CurrencyInput";
import { NumberInput } from "../../Controls/NumberInput";
import { TextAreaInput } from "../../Controls/TextAreaInput";
import getDate from "../../../utils/helpers/getDate";
import DateTimeInput from "../../Controls/DateTimeInput";
import { SelectInput } from "../../Controls/SelectInput";
import parseDecimal from "../../../utils/helpers/parseDecimal";
import { InputDisabled } from "../../Controls/InputDisabled";
import moment from "moment";
import {
  ALERT_ACTIONS,
  ALERT_STATUSES,
  useAlert,
} from "../../../context/AlertProvider";
import {
  useModalHistory,
  ACTIONS as MODAL_ACTIONS,
} from "../../../context/ModalHistoryProvider";
import {
  useDealsContext,
  ACTIONS as DEAL_ACTIONS,
} from "../../../context/DealsProvider";
import {
  useSystemContext,
} from "../../../context/SystemProvider";
import { useMemo } from "react";
import { CancelButton } from "../../Controls/CancelButton";
import selectValidators from "../../../utils/helpers/selectValidators";
import inputValidators from "../../../utils/helpers/inputValidators";
import Popup from "../../Popup/Popup";
import { IconButton, Radio, RadioGroup, Tooltip } from "@mui/material";
import { ConfirmButton } from "../../Controls/ConfirmButton";
import DuplicateDealFoundPopup from "../../Popups/DuplicateDealFound";
import MultiCarDataPubResultsPopup from "../../Popups/MultiCarDataPubResults";
import TotalCostOfCredit from "../../Controls/TotalCostOfCredit";
import { CurrencyInputDisabled } from "../../Controls/CurrencyInputDisabled";
import calculateTotalCostOfCredit from "../../../utils/helpers/calculateTotalCostOfCredit";
import calculateVAT, { VAT_TYPES } from "../../../utils/helpers/calculateVAT";
import useToast, { TOAST_TYPES } from "../../../utils/hooks/useToast";
import { Info } from "phosphor-react";
import colors from "../../../utils/constants/COLORS";
import DealAuditHistory from "../../Popups/DealAuditHistory";

export default function CreateEditNewSchemeDealModal({ scope, dealInfo }) {
  const user = get(USER_KEY);

  const [, quoteCreateDateInit] = getDate(new Date());
  const today = moment();
  today.add(2, "months");
  const lastDayOfNextMonth = new Date(today.year(), today.month(), 0); // 1 Month ahead, last day of month
  const [, startDateInit] = getDate(new Date());
  const [, endDateInit] = getDate(lastDayOfNextMonth);

  const [mmCode, setMMCode] = useType("string", "");
  const [brand, setBrand] = useType("string", "");
  const [range, setRange] = useType("string", "");
  const [model, setModel] = useType("string", "");
  const [dealStatus, setDealStatus] = useType("string", DEAL_STATUSES.ACTIVE);
  const [dealRequestStatus, setDealRequestStatus] = useType(
    "string",
    CONDITIONS.NEW
  );
  const [quoteCreateDate, setQuoteCreateDate] = useState(quoteCreateDateInit);
  const [quoteCreateDateUI, setQuoteCreateDateUI] =
    useState(quoteCreateDateInit);
  const [startDate, setStartDate] = useState(startDateInit);
  const [startDateUI, setStartDateUI] = useState(startDateInit);
  const [endDate, setEndDate] = useState(endDateInit);
  const [endDateUI, setEndDateUI] = useState(endDateInit);
  const [dealName, setDealName] = useType("string", "");
  const [instalment, setInstalment] = useType("number", null);
  const [retailIncl, setRetailIncl] = useType("number", null);
  const [vin, setVin] = useType("string", null);
  const [transmission, setTransmission] = useType("string", null);
  const [fuelType, setFuelType] = useType("string", null);
  const [rateType, setRateType] = useType("string", null);
  const [ratePercent, setRatePercent] = useType("number", null);
  const [deposit, setDeposit] = useType("number", null);
  const [period, setPeriod] = useType("number", null);
  const [balloonPercent, setBalloonPercent] = useType("number", 35);
  const [balloonAmount, setBalloonAmount] = useType("number", null);
  const [totalCostOfCredit, setTotalCostOfCredit] = useType("number", null);
  const [dealAssistance, setDealAssistance] = useType("number", null);
  const [notes, setNotes] = useType("string", "");
  // vehiclePrice * balloonPercent
  const [carID, setCarID] = useType("string", null);

  const [shouldDuplicate, setShouldDuplicate] = useState(null);
  const [prepopulateItem, setPrepopulateItem] = useType("object", {});
  const newDemoUsed = 1;
  const [mayProceed, setMayProceed] = useType("boolean", false);
  const [isSaving, setIsSaving] = useType("boolean", false);

  const anchorRef = useRef();

  const [showMMCodeModal, setShowMMCodeModal] = useType("boolean", false);
  const [showDealAuditHistory, setShowDealAuditHistory] = useType("boolean", false);

  const [showDuplicateDealFound, setShowDuplicateDealFound] = useType(
    "boolean",
    false
  );

  const [addToast] = useToast();
  const [, dispatchDeals] = useDealsContext();
  const [, dispatchModalHistory] = useModalHistory();
  const [{ vat },] = useSystemContext();
  const popAll = () => dispatchModalHistory({ type: MODAL_ACTIONS.POP_ALL });

  const [fetchBrands, brands, isFetchingBrands] = useAsync(
    useCallback(() => api.getBrands(true), []),
    "",
    "Issue fetching brands."
  );
  const brandID = brands?.find((b) => b.brand === brand)?.brand_ID;
  const [fetchRanges, ranges, isFetchingRanges] = useAsync(
    useCallback(() => api.getRanges(brandID, 1), [brandID]),
    "",
    "Issue fetching ranges."
  );
  const rangeID = ranges?.find((r) => r.range === range)?.range_ID;
  const [fetchModels, models, isFetchingModels] = useAsync(
    useCallback(() => api.getModels(rangeID, 1), [rangeID]),
    "",
    "Issue fetching models."
  );

  // Get brands
  useEffect(() => {
    fetchBrands(true);
  }, [fetchBrands]);

  // Handle deal name
  useEffect(() => {
    setDealName(
      `Total: R ${totalCostOfCredit}, Instalment: R ${instalment}, Rate: % ${ratePercent}`
    );
  }, [totalCostOfCredit, instalment, ratePercent, setDealName]);

  // Prepopulation based on Brand, Range, Model response
  useEffect(() => {
    if (hasNoValue(prepopulateItem)) return;
    console.log('prepopulateItem', prepopulateItem);

    (() => {
      setBrand(prepopulateItem?.brand || "");
      setRange(prepopulateItem?.range || "");
      setModel(prepopulateItem?.model || "");
      setMMCode(prepopulateItem?.mmCode || "");
      setCarID(prepopulateItem?.model_ID || "");
      setRetailIncl(parseDecimal(prepopulateItem?.price));
      setBalloonAmount(parseDecimal(prepopulateItem?.price) * (balloonPercent / 100));
      //setVin(prepopulateItem?.vinNumber);
      setTransmission(prepopulateItem?.transmission || "");
      setFuelType(prepopulateItem?.fuel || "");
    })();
  }, [
    prepopulateItem,
    setBrand,
    setRange,
    setModel,
    setMMCode,
    setRetailIncl,
    balloonPercent,
    setBalloonAmount,
    setCarID,
    setVin,
    setTransmission,
    setFuelType
  ]);

  useEffect(() => {
    setBalloonAmount(parseDecimal(retailIncl) * (balloonPercent / 100));
  }, [retailIncl, balloonPercent, setBalloonAmount]);

  // Prepopulation based on ViewAndEdit
  useEffect(() => {
    if (hasNoValue(dealInfo)) return;
    console.log('dealInfo', dealInfo);

    (() => {
      setMMCode(dealInfo?.mM_Code || "");
      setBrand(dealInfo?.brand || "");
      setRange(dealInfo?.range || "");
      setModel(dealInfo?.model || "");
      setDealStatus(dealInfo?.deal_Status || "");
      setCarID(dealInfo?.car_ID || "");
      setDealRequestStatus(dealInfo?.deal_Request_Status || "");
      setQuoteCreateDate(dealInfo?.quote_Create_Date || "");
      const [, quoteCreateDateInitUI] = getDate(
        dealInfo?.quote_Create_Date || ""
      );
      setQuoteCreateDateUI(quoteCreateDateInitUI);
      setStartDate(dealInfo?.quote_Reusable_Start_Date || "");
      const [, quoteReuseableStartDateInitUI] = getDate(
        dealInfo?.quote_Reusable_Start_Date || ""
      );
      setStartDateUI(quoteReuseableStartDateInitUI);
      setEndDate(dealInfo?.quote_Reusable_End_Date || "");
      const [, quoteReuseableEndDateInitUI] = getDate(
        dealInfo?.quote_Reusable_End_Date || ""
      );
      setEndDateUI(quoteReuseableEndDateInitUI);
      setDealName(dealInfo?.deal_Name || "");
      setNotes(dealInfo?.notes || "");
      setDealAssistance(parseDecimal(dealInfo?.deal_Assistance));
      setInstalment(parseDecimal(dealInfo?.instalment));
      setRetailIncl(parseDecimal(dealInfo?.retail_Incl));
      setRateType(dealInfo?.rate_Type || "");
      setRatePercent(parseDecimal(dealInfo?.rate_Percent));
      setDeposit(parseDecimal(dealInfo?.deposit));
      setPeriod(parseInt(dealInfo?.period) || 0);
      setBalloonPercent(parseDecimal(dealInfo?.balloon_Percent));
      setBalloonAmount(parseDecimal(dealInfo?.balloon_Amount));
      setTotalCostOfCredit(parseDecimal(dealInfo?.total_Cost_Of_Credit));
      setTransmission(dealInfo?.transmission || "");
      setFuelType(dealInfo?.fuel_Type || "");
    })();
  }, [
    dealInfo,
    setMMCode,
    setBrand,
    setRange,
    setModel,
    setDealStatus,
    setCarID,
    setDealRequestStatus,
    setQuoteCreateDate,
    setStartDate,
    setEndDate,
    setDealName,
    setNotes,
    setDealAssistance,
    setInstalment,
    setRetailIncl,
    setRateType,
    setRatePercent,
    setDeposit,
    setPeriod,
    setBalloonPercent,
    setBalloonAmount,
    setTotalCostOfCredit,
    setVin,
    setTransmission,
    setFuelType
  ]);

  // Handle TCOC
  useEffect(() => {
    const tcoc = calculateTotalCostOfCredit(
      retailIncl,
      ratePercent,
      deposit,
      balloonPercent,
      period
    );
    setTotalCostOfCredit(tcoc);
  }, [
    retailIncl,
    ratePercent,
    deposit,
    balloonPercent,
    period,
    setTotalCostOfCredit,
  ]);

  // Handle required fields
  const requiredFields = useMemo(
    () => ({
      mmCode: {
        value: mmCode,
        validate: () => (!mmCode ? "Please fill out this field" : ""),
      },
      brand: {
        value: brand,
        validate: (option) =>
          !brand && !option ? "Please fill out this field" : "",
      },
      range: {
        value: range,
        validate: (option) =>
          !range && !option ? "Please fill out this field" : "",
      },
      model: {
        value: model,
        validate: (option) =>
          !model && !option ? "Please fill out this field" : "",
      },
      dealStatus: {
        value: dealStatus,
        validate: (option) =>
          !dealStatus && !option ? "Please fill out this field" : "",
      },
      startDate: {
        value: startDate,
        validate: (option) =>
          !startDate && !option
            ? "Please fill out this field"
            : "",
      },
      endDate: {
        value: endDate,
        validate: (option) =>
          !endDate && !option
            ? "Please fill out this field"
            : "",
      },
      retailIncl: {
        value: retailIncl,
        validate: (option) => {
          if (!retailIncl && !option) return "Please fill out this field";
          return "";
        },
      },
      instalment: {
        value: instalment,
        validate: (option) => {
          if (!instalment && !option) return "Please fill out this field";
          return "";
        },
      },
      ratePercent: {
        value: ratePercent,
        validate: (option) => {
          if (!ratePercent && !option) return "Please fill out this field";
          return "";
        },
      },
      rateType: {
        value: rateType,
        validate: (option) =>
          !rateType && !option ? "Please fill out this field" : "",
      },
      deposit: {
        value: deposit,
        validate: (option) => {
          if (isNaN(deposit) && isNaN(option)) return "Please fill out this field";
          return "";
        },
      },
      period: {
        value: period,
        validate: (option) => {
          if (!period && !option) return "Please fill out this field";
          return "";
        },
      },
      balloonPercent: {
        value: balloonPercent,
        validate: (option) => {
          if (isNaN(balloonPercent) && isNaN(option)) return "Please fill out this field";
          return "";
        },
      },
      balloonAmount: {
        value: balloonAmount,
        validate: (option) => {
          if (isNaN(balloonAmount) && isNaN(option)) return "Please fill out this field";
          return "";
        },
      },
    }),
    [
      mmCode,
      brand,
      range,
      model,
      dealStatus,
      startDate,
      endDate,
      retailIncl,
      instalment,
      ratePercent,
      rateType,
      deposit,
      period,
      balloonPercent,
      balloonAmount,
    ]
  );

  // Handle validations
  useEffect(() => {
    if (
      Object.values(requiredFields).filter(
        (field) => field.validate().length > 0
      ).length > 0
    ) {
      setMayProceed(false);
    } else {
      setMayProceed(true);
    }
  }, [requiredFields, setMayProceed]);

  // Handle inserting new deal
  const handleDealInsert = async (data) => {
    const resp = await api.insertNewSchemeDeal(data);
    console.log("insertNewSchemeDeal", resp);
    if (resp?.success) {
      dispatchDeals({
        type: DEAL_ACTIONS.REFETCH_DEALS,
        payload: true,
      });
      popAll();
    } else {
      setIsSaving(false);
      addToast(TOAST_TYPES.ERROR, "Error creating deal.");
    }
  };

  // Handle updating new deal
  const handleDealUpdate = async (data) => {
    const resp = await api.updateNewSchemeDeal(data);
    console.log("updateNewSchemeDeal", resp);
    if (resp?.success) {
      dispatchDeals({
        type: DEAL_ACTIONS.REFETCH_DEALS,
        payload: true,
      });
      popAll();
    } else {
      setIsSaving(false);
      addToast(TOAST_TYPES.ERROR, "Error updating deal.");
    }
  };

  // Handle updating/inserting new deal
  const handleSubmit = async () => {
    setIsSaving(true);
    // Search duplicates
    const duplicateDealChecker = await api.getDealByModelIDAndType(
      carID,
      DEAL_TYPES.INSTALMENT_PRICE,
      parseDecimal(deposit),
      parseInt(period)
    );

    const isDuplicateDeal =
      duplicateDealChecker?.message === API_RESPONSE.PRELOADEDDEAL_FOUND;
    console.log("isDuplicateDeal", isDuplicateDeal);

    const data = {
      UserID: parseInt(get(USER_KEY)?.userID),
      CarID: carID,
      DealStatus:
        dealStatus === DEAL_STATUSES.ACTIVE ? "QuoteApproved" : "QuoteStopped",
      QuoteReusableStartDate: startDate,
      QuoteReusableEndDate: endDate,
      DealName: dealName,
      Notes: notes,
      SearchBarcode: `${brand}_${range}_${model}_${instalment}_${mmCode}_${notes}`,
      DealAssistance: parseDecimal(dealAssistance),
      Instalment: parseDecimal(instalment),
      RetailIncl: parseDecimal(retailIncl),
      RateType: rateType,
      RatePercent: parseDecimal(ratePercent),
      Deposit: parseDecimal(deposit),
      Period: parseInt(period) || 0,
      BalloonPercent: parseDecimal(balloonPercent),
      BalloonAmount: parseDecimal(balloonAmount),
      TotalCostOfCredit: parseDecimal(totalCostOfCredit),
      DealJSON: JSON.stringify({
        "dealname-id": dealName,
        submitType: "goLive",
        currentDealType: "Live",
        PreloadedDealsID: "", // Will only have if updating deal
        DealStatus: dealStatus,
        ToggleStatus: "",
        dealer_comments: notes,
        quoteValidity: "",
        PLDType: "InstalmentPrice",
        "car-section": {
          brand: brand,
          range: range,
          model: model,
          gearshift: transmission,
          fueltype: fuelType, 
          variant: carID,
          dealer_id: "",
          user_id: get(USER_KEY)?.userID,
          car_status: "New",
          mileage: "0",
          vin: vin,
          color: "Various" 
        },
        "options-section": [],
        "additional-fees": [],
        "price-section": {
          standard_price: parseDecimal(calculateVAT(VAT_TYPES.EXCL, retailIncl, vat)),
          discount: 0,
          discount_percent: 0,
          dealer_price: 0,
          total_extras: 0,
          on_road_costs: 0,
          other_costs: 0,
          othercosts_reason: "",
          total_excl: parseDecimal(calculateVAT(VAT_TYPES.EXCL, retailIncl, vat)),
          nonVAT_costs: 0,
          nonVAT: 0,
          vat: 0,
          total_total: parseDecimal(retailIncl),
          "spotter-fee": 0
        },
        "delivery-section": {},
        dealer_attachments: [],
        "validity-section": {
          quoteSentDate: "",
          quoteValidFor: "1",
          quoteToOtherCustomers: "2",
          quoteReusableStartDate: startDate,
          quoteReusableEndDate: endDate,
          internalNotes: "Portal_Upload",
          NoStock: "",
          NoStockUntilDate: "",
          NoStockComments: ""
        },
        totals: {
          standard_price: 0,
          discount: 0,
          discount_percent: 0,
          price: 0,
          on_road_costs: 0,
          other_costs: 0,
          extras: 0,
          excl: 0,
          total: 0,
          vat: 0,
          nonVAT: 0,
          incl: 0,
          trade_in_offer: 0,
          trade_in_assist: 0,
          trade_in: 0,
          net: 0
        },
        instalment: {
          Instalment: instalment,
          InstalmentFee: 0,
          Instalment_ExclFee: instalment,
          Instalment_InclFee: instalment,
          Retail_Incl: retailIncl,
          RateType: rateType,
          RatePercent: ratePercent,
          Deposit: deposit,
          Period: period,
          BalloonPercent: balloonPercent,
          BalloonAmount: parseDecimal(balloonAmount),
          TotalCostOfCredit: parseDecimal(totalCostOfCredit),
          OEMSubversion: 0,
          DealerSubversion: 0,
          PIC_Code: "",
          ApprovedCAT: "",
          SchemeDealName: dealName,
          Comments: notes
        },
        dealer_comments_excerpt: notes
      })
    };

    console.log("data", data);
    console.log("shouldDuplicate", shouldDuplicate);

    if (scope === SCOPES.CREATE) {
      if (isDuplicateDeal && shouldDuplicate === null) {
        setShowDuplicateDealFound(true);
      } else if (
        (isDuplicateDeal && shouldDuplicate === true) ||
        !isDuplicateDeal
      ) {
        handleDealInsert(data);
      } else if (isDuplicateDeal && shouldDuplicate === false) {
        const updateData = {
          ...data,
          PreloadedDealID: duplicateDealChecker?.preloadedDeal_ID,
        };
        await handleDealUpdate(updateData);
        insertDealAudit();
      }
    } else if (scope === SCOPES.EDIT) {
      const updateData = {
        ...data,
        PreloadedDealID: dealInfo?.preloaded_Deals_ID,
      };
      await handleDealUpdate(updateData);
      insertDealAudit();
    }
  };

  function insertDealAudit() {
    const oldData = {
      // UserID: parseInt(get(USER_KEY)?.userID),

      // CarID: dealInfo?.car_ID,
      MMCode: dealInfo?.mM_Code,
      Brand: dealInfo?.brand,
      Range: dealInfo?.range,
      Model: dealInfo?.model,

      DealStatus: dealInfo?.deal_Status === DEAL_STATUSES.ACTIVE ? "QuoteApproved" : "QuoteStopped",
      QuoteReusableStartDate: dealInfo?.quote_Reusable_Start_Date,
      QuoteReusableEndDate: dealInfo?.quote_Reusable_End_Date,
      // DealName: dealInfo?.deal_Name,
      Notes: dealInfo?.notes,
      // SearchBarcode: dealInfo?.search_Barcode,
      DealAssistance: dealInfo?.deal_Assistance,
      Instalment: dealInfo?.instalment,
      RetailIncl: dealInfo?.retail_Incl,
      RateType: dealInfo?.rate_Type,
      RatePercent: dealInfo?.rate_Percent,
      Deposit: dealInfo?.deposit,
      Period: dealInfo?.period,
      BalloonPercent: dealInfo?.balloon_Percent,
      BalloonAmount: dealInfo?.balloon_Amount,
      // TotalCostOfCredit: dealInfo?.total_Cost_Of_Credit,
    };

    const newData = {
      // UserID: parseInt(get(USER_KEY)?.userID),
      
      // CarID: carID,

      MMCode: mmCode,
      Brand: brand,
      Range: range,
      Model: model,

      DealStatus:
        dealStatus === DEAL_STATUSES.ACTIVE ? "QuoteApproved" : "QuoteStopped",
      QuoteReusableStartDate: startDate,
      QuoteReusableEndDate: endDate,
      // DealName: dealName,
      Notes: notes,
      // SearchBarcode: `${brand}_${range}_${model}_${instalment}_${mmCode}_${notes}`,
      DealAssistance: parseDecimal(dealAssistance),
      Instalment: parseDecimal(instalment),
      RetailIncl: parseDecimal(retailIncl),
      RateType: rateType,
      RatePercent: parseDecimal(ratePercent),
      Deposit: parseDecimal(deposit),
      Period: parseInt(period) || 0,
      BalloonPercent: parseDecimal(balloonPercent),
      BalloonAmount: parseDecimal(balloonAmount),
      // TotalCostOfCredit: parseDecimal(totalCostOfCredit),
    };

    if (!hasNoValue(dealInfo)) {
      console.log('dealInfo', dealInfo);
      console.log('oldData', oldData);
      console.log('newData', newData);
      const differences = getObjectDiff(oldData, newData);
      console.log('differences', differences);
      /*
      public int PreloadedDealID { set; get; }
      public string UserEmail { set; get; }
      public string Operation { set; get; }
      public string Change { set; get; }
      public string OldValue { set; get; }
      public string NewValue { set; get; }
      public string DealType { set; get; }
      */
      differences.forEach(async key => {
        const dataMap = {
          PreloadedDealID: dealInfo?.preloaded_Deals_ID,
          UserEmail: user?.emailAddress,
          Operation: "Update",
          Change: key,
          OldValue: oldData?.[key]?.toString(),
          NewValue: newData?.[key]?.toString(),
          DealType: "InstallmentPrice"
        }
        console.log('DealAudit dataMap', dataMap);

        await api.insertDealAudit(dataMap);
      });
    }
  }

  return (
    <Modal
      size={{ width: "95%", height: "auto" }}
      title={
        showMMCodeModal
          ? "Search MM Code"
          : `You are now ${scope === SCOPES.EDIT ? "editing" : "creating"
          } a Scheme deal (New) ${scope === SCOPES.EDIT ? `(${dealInfo?.preloaded_Deals_ID})` : ""}`
      }
      icon={
        hasNoValue(dealInfo) ? (
          <Info size={20} color={colors.informational.color} />
        ) :(
            <Tooltip title="Deal update history">
              <IconButton onClick={() => setShowDealAuditHistory(true)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 256 256">
                  <path fill="currentColor" d="M136 80v43.47l36.12 21.67a8 8 0 0 1-8.24 13.72l-40-24A8 8 0 0 1 120 128V80a8 8 0 0 1 16 0m-8-48a95.44 95.44 0 0 0-67.92 28.15C52.81 67.51 46.35 74.59 40 82V64a8 8 0 0 0-16 0v40a8 8 0 0 0 8 8h40a8 8 0 0 0 0-16H49c7.15-8.42 14.27-16.35 22.39-24.57a80 80 0 1 1 1.66 114.75a8 8 0 1 0-11 11.64A96 96 0 1 0 128 32" />
                </svg>
              </IconButton >
          </Tooltip>
        )
      }
    >
      {showMMCodeModal ? (
        <SearchModal
          searchFor="MMCode"
          newDemoUsed={newDemoUsed}
          value={mmCode}
          setValue={setMMCode}
          setResultItem={setPrepopulateItem}
          setShowSearchModal={setShowMMCodeModal}
        />
      ) : (
          <StackStyle padding="8px 0" gap="20px" ref={anchorRef}>
          <QueueStyle gap="10px" justifyContent="start">
            <MMCodeInput
              value={mmCode}
              setValue={setMMCode}
              setShowMMCodeModal={setShowMMCodeModal}
              validate={requiredFields.mmCode.validate}
            />
            <BrandSelect
              brands={brands}
              setBrand={setBrand}
              setRange={setRange}
              setModel={setModel}
              fetchRanges={fetchRanges}
              brand={brand}
              isFetchingBrands={isFetchingBrands}
              validate={requiredFields.brand.validate}
            />
            <RangeSelect
              ranges={ranges}
              setRange={setRange}
              setModel={setModel}
              fetchModels={fetchModels}
              range={range}
              isFetchingRanges={isFetchingRanges}
              validate={requiredFields.range.validate}
            />
            <ModelSelect
              models={models}
              setModel={setModel}
              setPrepopulateItem={setPrepopulateItem}
              model={model}
              isFetchingModels={isFetchingModels}
              validate={requiredFields.model.validate}
            />
          </QueueStyle>
          <QueueStyle gap="10px" justifyContent="start">
            <InputDisabled
              label="Deal name (Auto populated)"
              value={dealName}
            />
            <SelectInput
              label="Deal status*"
              options={Object.values(DEAL_STATUSES)}
              option={dealStatus}
              setOption={setDealStatus}
              validate={requiredFields.dealStatus.validate}
            />
            <InputDisabled
              label="Deal request status"
              value={dealRequestStatus}
            />
          </QueueStyle>
          <QueueStyle gap="10px" justifyContent="start">
            <DateTimeInput
              label="Quote create date"
              value={quoteCreateDateUI}
              setValue={setQuoteCreateDate}
              setUIValue={setQuoteCreateDateUI}
              disabled={true}
              validate={() => ""}
            />
            <DateTimeInput
              label="Quote start date*"
              value={startDateUI}
              setValue={setStartDate}
              setUIValue={setStartDateUI}
              validate={requiredFields.startDate.validate}
            />
            <DateTimeInput
              label="Quote end date*"
              value={endDateUI}
              setValue={setEndDate}
              setUIValue={setEndDateUI}
              validate={requiredFields.endDate.validate}
            />
          </QueueStyle>
          <QueueStyle gap="10px" justifyContent="start">
            <CurrencyInput
              label="Retail*"
              value={retailIncl}
              setValue={setRetailIncl}
              suffix="(Incl VAT)"
              initialValue={parseDecimal(dealInfo?.retail_Incl)}
              validate={requiredFields.retailIncl.validate}
            />
            <CurrencyInput
              label="Instalment*"
              value={instalment}
              setValue={setInstalment}
              suffix="R"
              initialValue={parseDecimal(dealInfo?.instalment)}
              validate={requiredFields.instalment.validate}
            />
            <CurrencyInput
              label="Deal assistance"
              value={dealAssistance}
              setValue={setDealAssistance}
              suffix="R"
              initialValue={parseDecimal(dealInfo?.deal_Assistance)}
              validate={() => ""}
            />
          </QueueStyle>
          <QueueStyle gap="10px" justifyContent="start">
            <NumberInput
              label="Rate*"
              value={ratePercent}
              setValue={setRatePercent}
              suffix="%"
              validate={requiredFields.ratePercent.validate}
            />
            <SelectInput
              label="Rate type*"
              options={Object.values(RATE_TYPES)}
              option={rateType}
              setOption={setRateType}
              validate={requiredFields.rateType.validate}
            />
            <NumberInput
              label="Deposit*"
              value={deposit}
              setValue={setDeposit}
              suffix="%"
              validate={requiredFields.deposit.validate}
            />
            <NumberInput
              label="Period*"
              value={period}
              setValue={setPeriod}
              suffix="(Months)"
              validate={requiredFields.period.validate}
            />
          </QueueStyle>
          <QueueStyle gap="10px" justifyContent="start">
            <NumberInput
              label="Balloon %*"
              value={balloonPercent}
              setValue={setBalloonPercent}
              suffix="%"
              validate={requiredFields.balloonPercent.validate}
            />
            <CurrencyInput
              label="Balloon*"
              value={balloonAmount}
              setValue={setBalloonAmount}
              suffix="R"
              initialValue={parseDecimal(dealInfo?.balloon_Amount)}
              validate={requiredFields.balloonAmount.validate}
            />
            <CurrencyInputDisabled
              label="Total cost of credit (Auto populated)"
              value={totalCostOfCredit}
              suffix="R"
            />
          </QueueStyle>
          <QueueStyle gap="10px">
            <TextAreaInput
              label="Notes that will display on the deal"
              value={notes}
              setValue={setNotes}
            />
          </QueueStyle>
          <DuplicateDealFoundPopup
            title={`Duplicate Scheme deal found. MM Code: ${mmCode}`}
            open={showDuplicateDealFound}
            onClose={() => {
              setShouldDuplicate(null);
              setShowDuplicateDealFound(false);
            }}
            checked={shouldDuplicate}
            setChecked={setShouldDuplicate}
            onConfirm={() => {
              setShowDuplicateDealFound(false);
              handleSubmit();
            }}
            />
            <DealAuditHistory
              open={showDealAuditHistory}
              onClose={() => setShowDealAuditHistory(false)}
              dealInfo={dealInfo}
              idKey={"preloaded_Deals_ID"}
            />
          <QueueStyle justifyContent="center">
            <CancelButton onClick={popAll} />
              <SubmitButton disabled={!mayProceed || isSaving} onClick={handleSubmit} isSaving={isSaving} />
          </QueueStyle>
        </StackStyle>
      )}
    </Modal>
  );
}
